import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { Home, Info, MessageCircle, Settings, Menu, X, ArrowRight, Code, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import AuthModal from './AuthModal'
import { SiteFooter } from './SiteFooter'
import { isStripeTestMode } from '../config/stripe'
import { LANGUAGES, loadLanguage, type LanguageCode } from '../i18n'

const warmPages = ['/', '/about', '/how-it-works', '/faq', '/contact']

const Layout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup')
  const location = useLocation()
  const { user } = useAuth()
  const { t, i18n } = useTranslation()
  const isWarm = warmPages.includes(location.pathname)

  const currentLang = (i18n.language?.split('-')[0] ?? 'en') as LanguageCode
  const activeLang = currentLang in LANGUAGES ? currentLang : 'en'

  const navigation = [
    { name: t('nav.home'), href: '/', icon: Home },
    { name: t('nav.how_it_works'), href: '/how-it-works', icon: Info },
    { name: t('nav.about'), href: '/about', icon: Info },
    { name: t('nav.faq'), href: '/faq', icon: MessageCircle },
    { name: t('nav.for_geeks'), href: '/for-geeks', icon: Code },
    { name: t('nav.contact'), href: '/contact', icon: MessageCircle },
  ]

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const handleAuthClick = (mode: 'signin' | 'signup') => {
    setAuthMode(mode)
    setAuthModalOpen(true)
  }

  return (
    <div className={`flex flex-col min-h-screen ${isWarm ? 'bg-warm-cream' : 'bg-dark'}`}>
      {/* Stripe test mode banner */}
      {isStripeTestMode && (
        <div className="bg-amber-500/90 text-black text-center text-xs font-semibold py-1 px-2">
          STRIPE TEST MODE — payments use sandbox keys
        </div>
      )}

      {/* Skip to main content (accessibility) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2
          focus:px-4 focus:py-2 focus:bg-cyan-500 focus:text-white focus:rounded-lg focus:text-sm focus:font-semibold"
      >
        {t('nav.skip_to_main')}
      </a>

      {/* Desktop + Mobile Header */}
      <header className={`sticky top-0 z-40 border-b backdrop-blur-xl ${
        isWarm
          ? 'border-warm-peach/40 bg-warm-cream/90'
          : 'border-white/[0.06] bg-dark/80'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2">
              <span className={`text-xl font-bold ${
                isWarm ? 'font-warm-display text-warm-coral' : 'font-display gradient-text'
              }`}>justfixit.AI</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isWarm
                      ? isActive(item.href)
                        ? 'text-warm-coral bg-warm-coral/10 font-warm-body'
                        : 'text-warm-navy/60 hover:text-warm-navy hover:bg-warm-navy/5 font-warm-body'
                      : isActive(item.href)
                        ? 'text-cyan-400 bg-cyan-500/10'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA / Auth */}
            <div className="hidden md:flex items-center gap-3">
              {/* Language switcher */}
              <label className="sr-only" htmlFor="lang-select">Choose a language</label>
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium ${
                isWarm ? 'text-warm-navy/60' : 'text-gray-400'
              }`}>
                <Globe size={14} aria-hidden="true" />
                <select
                  id="lang-select"
                  value={activeLang}
                  onChange={(e) => void loadLanguage(e.target.value as LanguageCode)}
                  className={`bg-transparent text-xs font-medium cursor-pointer focus-visible:outline-2 focus-visible:outline-cyan-400 ${
                    isWarm ? 'text-warm-navy/70' : 'text-gray-300'
                  }`}
                >
                  {Object.entries(LANGUAGES).map(([code, label]) => (
                    <option key={code} value={code} className="text-black">{label}</option>
                  ))}
                </select>
              </div>

              {user ? (
                <Link
                  to="/dashboard"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                    isWarm
                      ? 'bg-warm-coral text-white font-warm-display hover:shadow-[0_4px_20px_rgba(255,107,107,0.3)]'
                      : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]'
                  }`}
                >
                  {t('nav.dashboard')}
                  <ArrowRight size={16} />
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => handleAuthClick('signin')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      isWarm ? 'text-warm-navy/50 hover:text-warm-navy font-warm-body' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {t('nav.sign_in')}
                  </button>
                  <a
                    href="/#join"
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                      isWarm
                        ? 'bg-warm-coral text-white font-warm-display hover:shadow-[0_4px_20px_rgba(255,107,107,0.3)]'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]'
                    }`}
                    onClick={(e) => {
                      // Early-access signup lives in the home page's #join section.
                      const target = document.getElementById('join')
                      if (target) {
                        e.preventDefault()
                        target.scrollIntoView({ behavior: 'smooth' })
                      }
                    }}
                  >
                    {t('nav.get_early_access')}
                    <ArrowRight size={16} />
                  </a>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className={`md:hidden p-2 rounded-lg transition-colors ${
                isWarm ? 'text-warm-navy/50 hover:text-warm-navy hover:bg-warm-navy/5' : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-72 bg-dark-50 border-l border-white/[0.06] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end mb-8">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05]"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {navigation.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
                      ${isActive(item.href)
                        ? 'text-cyan-400 bg-cyan-500/10'
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                      }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon size={18} />
                    <span>{item.name}</span>
                  </Link>
                )
              })}
              <div className="border-t border-white/[0.06] my-3" />
              <Link
                to="/legal-faq"
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/[0.05] transition-all duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.legal_faq')}
              </Link>
              {/* Mobile language switcher */}
              <label className="sr-only" htmlFor="lang-select-mobile">Choose a language</label>
              <div className="flex items-center justify-center gap-2 px-4 py-3">
                <Globe size={16} className="text-gray-400" aria-hidden="true" />
                <select
                  id="lang-select-mobile"
                  value={activeLang}
                  onChange={(e) => void loadLanguage(e.target.value as LanguageCode)}
                  className="bg-transparent text-sm font-medium text-gray-300 cursor-pointer"
                >
                  {Object.entries(LANGUAGES).map(([code, label]) => (
                    <option key={code} value={code} className="text-black">{label}</option>
                  ))}
                </select>
              </div>
              {!user && (
                <button
                  onClick={() => { handleAuthClick('signin'); setIsMobileMenuOpen(false) }}
                  className="mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold
                    bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                >
                  {t('nav.get_early_access')}
                  <ArrowRight size={16} />
                </button>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
      />

      {/* Main Content */}
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <SiteFooter tone={isWarm ? 'warm' : 'dark'} />

      {/* Bottom Navigation (Mobile) */}
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 z-30 backdrop-blur-xl border-t ${
        isWarm ? 'bg-white/90 border-warm-peach/40' : 'bg-dark/90 border-white/[0.06]'
      }`}>
        <div className="flex justify-around py-2">
          {[
            { name: t('nav.home'), href: '/', icon: Home },
            { name: t('nav.how_it_works'), href: '/how-it-works', icon: Info },
            { name: t('nav.contact'), href: '/contact', icon: MessageCircle },
            { name: t('nav.settings'), href: '/settings', icon: Settings },
          ].map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 min-w-[60px] transition-colors duration-200
                  ${isWarm
                    ? isActive(item.href) ? 'text-warm-coral' : 'text-warm-navy/40 hover:text-warm-navy/70'
                    : isActive(item.href) ? 'text-cyan-400' : 'text-gray-500 hover:text-gray-300'
                  }`}
              >
                <Icon size={20} />
                <span className="text-[10px] font-medium">{item.name}</span>
              </Link>
            )
          })}
        </div>
      </nav>

      {/* Mobile bottom padding */}
      <div className="md:hidden h-16" />
    </div>
  )
}

export default Layout
